<template>
  <div data-testid="content-edit-regenerate-content">
    <p class="mb-2 opacity-80 uppercase">{{ $t("regenerate") }}</p>
    <div
      v-if="isPending"
      class="h-40 bg-surface-200/10 dark:bg-surface-700/30 flex flex-col items-center justify-center text-center border dark:border-surface-700 rounded-md"
    >
      <i class="pi pi-sparkles text-lg animate-[bounce_1s_400ms_ease-in-out_infinite]" />

      <p class="text-sm mt-1">{{ $t("regeneratingContent") }}</p>

      <div class="py-4 w-3/4">
        <ProgressBar :value="progress" :show-value="false" style="height: 5px" />
      </div>
    </div>
    <div v-else>
      <div class="grid grid-cols-2 gap-2">
        <ContentEditRegenerateButton
          v-for="tone in tones"
          :key="tone"
          :label="tone"
          icon="pi-sparkles"
          class="w-full tone"
          data-testid="content-edit-regenerate-button"
          @click="regenerateContent(tone)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "vue-toast-notification";
import ContentEditRegenerateButton from "./ContentEditRegenerateButton.vue";
import ProgressBar from "@/volt/ProgressBar.vue";
import { useI18n } from "vue3-i18n";

const props = defineProps<{
  tones: string[];
  contentId?: string;
}>();

const emit = defineEmits<{
  compare: [string];
}>();

const route = useRoute();
const currentContentId = computed(() => {
  const id = props.contentId || route.query.contentId;
  return typeof id === "string" ? id : "";
});
const $toast = useToast(defaultToastOptions);
const { t } = useI18n();
const { mutate, isPending } = useContentRegenerate();
const { progress, endProgress, startProgress, setProgress } = useFakeProgress({
  maxValue: 99,
  increments: 5,
  minValue: 5,
  interval: 700,
  auto: false,
});

function regenerateContent(tone: string) {
  if (isPending.value || !currentContentId.value) return;
  startProgress();
  setProgress(15);
  mutate(
    {
      contentId: currentContentId.value,
      tone,
    },
    {
      onSettled() {
        setProgress(99);
        endProgress();
      },
      onError(e) {
        const message = e.message || t("errors.tryAgain");
        $toast.error(message);
      },
      onSuccess(data) {
        if (data) {
          emit("compare", data);
          $toast.success(t("wordsRegenerated"));
        }
      },
    }
  );
}
</script>
